import React from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import CalendarViewWeekOutlinedIcon from '@mui/icons-material/CalendarViewWeekOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined';
import LinkOffOutlinedIcon from '@mui/icons-material/LinkOffOutlined';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';

/**
 * SummaryCard - Card individual de KPI (mesmo visual de CompanySummaryCards/SubscriptionSummaryCards)
 */
function SummaryCard({ title, subtitle, value, caption, icon, iconColor, iconBg }) {
  return (
    <Card
      sx={{
        borderRadius: '12px',
        boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
        border: '1px solid #E8ECF0',
        p: '20px',
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        transition: 'box-shadow 0.2s ease',
        '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.1)' },
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, fontSize: '0.8125rem' }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mb: 0.5 }}>
              {subtitle}
            </Typography>
          )}
          <Typography
            variant="h4"
            sx={{ fontWeight: 700, fontSize: '1.75rem', color: 'text.primary', lineHeight: 1, mt: subtitle ? 0 : 0.5 }}
          >
            {value}
          </Typography>
          {caption && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              {caption}
            </Typography>
          )}
        </Box>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: '10px',
            bgcolor: iconBg,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {React.cloneElement(icon, { sx: { color: iconColor, fontSize: 20 } })}
        </Box>
      </Box>
    </Card>
  );
}

/**
 * WhatsAppSummaryCards - Cards de KPI no topo da tela "WhatsApp" (Admin Geral)
 * @param {Object} props
 * @param {Object} props.overview - retorno de overview.php
 * @param {number} props.days - período selecionado, usado nos cards que variam com o filtro
 */
function WhatsAppSummaryCards({ overview, days }) {
  const isMobile = useMediaQuery('(max-width: 900px)');
  const o = overview || {};
  const sends = o.platform_sends || {};
  const byOrigin = o.companies_by_origin || {};
  const blocked = o.blocked_in_period || {};
  const alerts = o.limit_alerts_in_period || {};
  const byLimit = alerts.by_limit || {};

  const blockedCaption = (blocked.policy_blocked || 0) > 0
    ? `+${blocked.policy_blocked} bloqueados por política`
    : null;

  const alertsCaption = (alerts.total || 0) > 0
    ? `diário: ${byLimit.per_day ?? 0} · por minuto: ${byLimit.per_minute ?? 0} · global diário: ${byLimit.global_per_day ?? 0} · global por minuto: ${byLimit.global_per_minute ?? 0}`
    : null;

  const cards = [
    {
      title: 'Envios pela plataforma hoje',
      value: sends.today ?? 0,
      icon: <SendOutlinedIcon />,
      iconColor: '#2E7D32',
      iconBg: '#E8F5E9',
    },
    {
      title: 'Envios pela plataforma',
      subtitle: 'últimos 7 dias',
      value: sends.last_7_days ?? 0,
      icon: <CalendarViewWeekOutlinedIcon />,
      iconColor: '#1565C0',
      iconBg: '#E3F2FD',
    },
    {
      title: 'Envios pela plataforma',
      subtitle: 'últimos 30 dias',
      value: sends.last_30_days ?? 0,
      icon: <CalendarMonthOutlinedIcon />,
      iconColor: '#1565C0',
      iconBg: '#E3F2FD',
    },
    {
      title: 'Empresas: plataforma / número próprio',
      value: `${byOrigin.platform ?? 0} / ${byOrigin.own_connected ?? 0}`,
      icon: <ApartmentOutlinedIcon />,
      iconColor: '#5A80FB',
      iconBg: 'rgba(90, 128, 251, 0.1)',
    },
    {
      title: 'Número próprio desconectado',
      value: byOrigin.own_disconnected ?? 0,
      icon: <LinkOffOutlinedIcon />,
      iconColor: '#C62828',
      iconBg: '#FFEBEE',
    },
    {
      title: 'Bloqueados por limite (429)',
      subtitle: `últimos ${days} dias`,
      value: blocked.rate_limited ?? 0,
      caption: blockedCaption,
      icon: <BlockOutlinedIcon />,
      iconColor: '#E65100',
      iconBg: '#FFF3E0',
    },
    {
      title: 'Alertas de limite diário',
      subtitle: `últimos ${days} dias`,
      value: alerts.total ?? 0,
      caption: alertsCaption,
      icon: <ReportProblemOutlinedIcon />,
      iconColor: '#E65100',
      iconBg: '#FFF3E0',
    },
  ];

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
        gap: 2,
        mb: 3,
      }}
    >
      {cards.map((card, index) => (
        <SummaryCard key={index} {...card} />
      ))}
    </Box>
  );
}

export default WhatsAppSummaryCards;
